import { WorkPage } from '../components/work-page.tsx';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { feedbackApi } from '../api/endpoints.ts';
import { useDocumentTitle } from '../hooks/index.ts';
import { Alert, Button, LoadingBlock, Select, TextArea, classes } from '../components/ui.tsx';
import { StatusPill } from './feedback.tsx';
import { formatShortInstant } from '../i18n/datetime.ts';

/**
 * One feature request, with its story so far.
 *
 * The timeline is the point of this page. A vote count says how many people
 * wanted something; the timeline says what happened next — published, planned,
 * being built, ready to try, released — which is the whole feedback loop this
 * feature exists to make visible (§51).
 */
export default function FeedbackDetailPage() {
  const { t } = useTranslation();
  const { requestId } = useParams<{ requestId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params] = useSearchParams();
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['feedback-detail', requestId],
    queryFn: () => feedbackApi.detail(requestId!),
    enabled: Boolean(requestId),
  });

  // A merged request answers with the id of the one that absorbed it (§13).
  // Replaced rather than pushed, so Back goes where the reader came from
  // instead of bouncing them through the redirect again.
  const redirectTo = query.data?.redirectTo;
  useEffect(() => {
    if (redirectTo) navigate(`/feedback/${redirectTo}`, { replace: true });
  }, [redirectTo, navigate]);

  const request = query.data?.request;
  useDocumentTitle(request?.title ?? t('feedback.documentTitle'));

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['feedback-detail', requestId] });
    void queryClient.invalidateQueries({ queryKey: ['feedback'] });
    void queryClient.invalidateQueries({ queryKey: ['feedback-state'] });
  };

  const vote = useMutation({
    mutationFn: () => (request?.hasVoted ? feedbackApi.unvote(requestId!) : feedbackApi.vote(requestId!)),
    onSuccess: invalidate,
    onError: (mutationError: Error) => setError(mutationError.message),
  });

  const follow = useMutation({
    mutationFn: () => (request?.isFollowing ? feedbackApi.unfollow(requestId!) : feedbackApi.follow(requestId!)),
    onSuccess: invalidate,
    onError: (mutationError: Error) => setError(mutationError.message),
  });

  const preview = useMutation({
    mutationFn: () => feedbackApi.startPreview(requestId!),
    onSuccess: (result) => {
      // A new tab, so the reader keeps their place on the board — and because
      // the preview is a different origin with a session of its own that has
      // no business replacing this one in the same tab's history.
      window.open(result.url, '_blank', 'noopener,noreferrer');
    },
    onError: (mutationError: Error) => setError(mutationError.message),
  });

  if (query.isLoading)
    return (
      <WorkPage title={t('feedback.title')}>
        <div className="card">
          <LoadingBlock label={t('feedback.loading')} />
        </div>
      </WorkPage>
    );
  if (query.isError || (!request && !redirectTo)) {
    return (
      <WorkPage title={t('feedback.title')}>
        <div className="card">
          <Alert tone="error" title={t('feedback.notFoundTitle')}>
            <p>{t('feedback.notFoundBody')}</p>
            <p className="mt-3">
              <Link to="/feedback" className="font-semibold underline">
                {t('feedback.backToBoard')}
              </Link>
            </p>
          </Alert>
        </div>
      </WorkPage>
    );
  }
  if (!request)
    return (
      <WorkPage title={t('feedback.title')}>
        <div className="card">
          <LoadingBlock label={t('feedback.loading')} />
        </div>
      </WorkPage>
    );

  return (
    <WorkPage
      title={request.title}
      actions={
        <>
          <Button
            variant={request.hasVoted ? 'secondary' : 'primary'}
            disabled={vote.isPending || request.status === 'deployed' || request.status === 'pending_moderation'}
            onClick={() => vote.mutate()}
          >
            {request.hasVoted
              ? t('feedback.votedWithCount', { count: request.votes })
              : t('feedback.voteWithCount', { count: request.votes })}
          </Button>
          <Button disabled={follow.isPending} onClick={() => follow.mutate()}>
            {request.isFollowing ? t('feedback.unfollow') : t('feedback.follow')}
          </Button>
        </>
      }
    >
      {error ? (
        <div className="mb-4">
          <Alert tone="error" title={t('feedback.actionErrorTitle')}>
            {error}
          </Alert>
        </div>
      ) : null}

      <div className="card mb-6">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <StatusPill status={request.customerStatus} />
          {request.category ? (
            <span className="badge border-[color:var(--color-line)] text-[color:var(--color-muted)]">
              {t(`feedback.category.${request.category}`)}
            </span>
          ) : null}
          <span className="text-sm text-[color:var(--color-muted)]">
            {t('feedback.submittedOn', { date: formatShortInstant(request.createdAt) })}
          </span>
        </div>

        <p className="whitespace-pre-wrap">{request.description}</p>

        {request.publicAdminResponse ? (
          <div className="mt-4 rounded-md border-l-4 border-[color:var(--color-brand)] bg-[color:var(--color-surface)] p-4">
            <p className="text-sm font-semibold">{t('feedback.officialResponse')}</p>
            <p className="mt-1 whitespace-pre-wrap text-sm">{request.publicAdminResponse}</p>
          </div>
        ) : null}

        {request.mergedTitles.length > 0 ? (
          <div className="mt-4 text-sm text-[color:var(--color-muted)]">
            <p className="font-semibold">{t('feedback.alsoAskedFor')}</p>
            <ul className="mt-1 list-disc pl-5">
              {request.mergedTitles.map((title) => (
                <li key={title}>{title}</li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>

      {request.previewAvailable ? (
        <div className="card mb-6 border-[color:var(--color-brand)]">
          <h2 className="text-lg font-semibold">{t('feedback.previewTitle')}</h2>
          <p className="mt-1 text-sm text-[color:var(--color-muted)]">
            {request.previewInstructions ?? t('feedback.previewDefaultInstructions')}
          </p>
          <p className="mt-2 text-sm text-[color:var(--color-muted)]">{t('feedback.previewSafetyNote')}</p>
          <p className="mt-4">
            <Button variant="primary" disabled={preview.isPending} onClick={() => preview.mutate()}>
              {preview.isPending ? t('feedback.previewOpening') : t('feedback.tryPreview')}
            </Button>
          </p>
        </div>
      ) : null}

      {request.status === 'preview' || request.previewResponse || params.get('respond') === '1' ? (
        <PreviewResponseCard
          requestId={request.id}
          question={request.previewQuestion}
          existing={request.previewResponse}
          onSaved={invalidate}
        />
      ) : null}

      {request.status === 'deployed' ? (
        <div className="card mb-6">
          <h2 className="text-lg font-semibold">
            {t('feedback.releasedHeading')}
            {request.releaseVersion ? ` · ${request.releaseVersion}` : ''}
          </h2>
          {request.deployedAt ? (
            <p className="mt-1 text-sm text-[color:var(--color-muted)]">
              {t('feedback.releasedOn', { date: formatShortInstant(request.deployedAt) })} ·{' '}
              {t('feedback.voteCount', { count: request.votes })}
            </p>
          ) : null}
          {request.releaseNotes ? <p className="mt-3 whitespace-pre-wrap">{request.releaseNotes}</p> : null}
        </div>
      ) : null}

      {request.timeline.length > 0 ? (
        <div className="card">
          <h2 className="text-lg font-semibold">{t('feedback.timelineTitle')}</h2>
          <ol className="mt-4 grid gap-4">
            {request.timeline.map((entry, index) => (
              <li key={`${entry.status}-${entry.createdAt}-${index}`} className="flex gap-3">
                <span
                  aria-hidden="true"
                  className={classes(
                    'mt-1.5 h-2 w-2 shrink-0 rounded-full',
                    index === request.timeline.length - 1
                      ? 'bg-[color:var(--color-brand)]'
                      : 'bg-[color:var(--color-line)]',
                  )}
                />
                <div>
                  <p className="text-sm font-semibold">{t(`feedback.status.${entry.customerStatus}`)}</p>
                  <p className="text-xs text-[color:var(--color-muted)]">{formatShortInstant(entry.createdAt)}</p>
                  {entry.note ? <p className="mt-1 text-sm">{entry.note}</p> : null}
                </div>
              </li>
            ))}
          </ol>
        </div>
      ) : null}
    </WorkPage>
  );
}

/**
 * "Looks good / Needs work / Leave feedback" (§26).
 *
 * Recorded here, on production, rather than inside the preview environment:
 * that tenant is disposable and anything written there disappears with it, and
 * the response belongs to the feature request rather than to the demo. The
 * banner inside the preview links back to this card.
 */
function PreviewResponseCard({
  requestId,
  question,
  existing,
  onSaved,
}: {
  requestId: string;
  question: string | null;
  existing: { rating: string; feedback: string | null; createdAt: string } | null;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [rating, setRating] = useState(existing?.rating ?? '');
  const [feedback, setFeedback] = useState(existing?.feedback ?? '');
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: () =>
      feedbackApi.respondToPreview(requestId, {
        rating: rating as 'positive' | 'neutral' | 'negative',
        feedback: feedback.trim() || null,
      }),
    onSuccess: () => {
      setSaved(true);
      onSaved();
    },
    onError: (mutationError: Error) => setError(mutationError.message),
  });

  return (
    <form
      className="card mb-6"
      onSubmit={(event) => {
        event.preventDefault();
        setError(null);
        if (rating) submit.mutate();
      }}
    >
      <h2 className="text-lg font-semibold">{t('feedback.responseTitle')}</h2>
      <p className="mt-1 text-sm text-[color:var(--color-muted)]">
        {question ?? t('feedback.responseDefaultQuestion')}
      </p>

      {saved ? (
        <div className="mt-4">
          <Alert tone="success" title={t('feedback.responseSavedTitle')}>
            {t('feedback.responseSavedBody')}
          </Alert>
        </div>
      ) : null}

      {error ? (
        <div className="mt-4">
          <Alert tone="error" title={t('feedback.actionErrorTitle')}>
            {error}
          </Alert>
        </div>
      ) : null}

      <div className="mt-4 grid gap-4 sm:max-w-md">
        <Select
          label={t('feedback.responseRatingLabel')}
          value={rating}
          options={[
            { value: '', label: t('feedback.responseRatingNone') },
            { value: 'positive', label: t('feedback.responsePositive') },
            { value: 'neutral', label: t('feedback.responseNeutral') },
            { value: 'negative', label: t('feedback.responseNegative') },
          ]}
          onChange={(event) => setRating(event.target.value)}
        />
        <TextArea
          label={t('feedback.responseNotesLabel')}
          rows={4}
          maxLength={2000}
          value={feedback}
          onChange={(event) => setFeedback(event.target.value)}
        />
        <div>
          <Button type="submit" variant="primary" disabled={!rating || submit.isPending}>
            {submit.isPending ? t('feedback.responseSending') : t('feedback.responseSend')}
          </Button>
        </div>
      </div>
    </form>
  );
}
